/**
 * CrewBulkActions Component
 * Bulk action controls for selected crew members
 */

import React, { useRef, useEffect } from 'react';
import { UserCheck, CheckCircle, XCircle, Clock, Download, Trash2, ChevronDown } from 'lucide-react';
import { exportCrewToCSV } from '../utils/crewHelpers';

const CrewBulkActions = ({ 
  selectedCrew, 
  crewMembers,
  onSignOn,
  onSignOff,
  onDelete,
  onClearSelection
}) => {
  const [showBulkActionsMenu, setShowBulkActionsMenu] = React.useState(false);
  const bulkActionsRef = useRef(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bulkActionsRef.current && !bulkActionsRef.current.contains(event.target)) {
        setShowBulkActionsMenu(false);
      }
    };
    
    if (showBulkActionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showBulkActionsMenu]);

  const handleBulkSignOn = () => {
    onSignOn(selectedCrew);
    setShowBulkActionsMenu(false);
  };
  
  const handleBulkSignOff = () => {
    onSignOff(selectedCrew);
    setShowBulkActionsMenu(false);
  };
  
  const handleBulkRecordHOR = () => {
    // This would open a modal to record hours for selected crew
    alert(`Record Hours of Rest for ${selectedCrew.length} crew members - Feature coming soon`);
    setShowBulkActionsMenu(false);
  };
  
  const handleBulkExport = () => {
    const selectedMembers = crewMembers.filter(m => selectedCrew.includes(m.id));
    exportCrewToCSV(selectedMembers, 'selected-crew');
    setShowBulkActionsMenu(false);
  };
  
  const handleBulkDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedCrew.length} crew members? This action cannot be undone.`)) {
      onDelete(selectedCrew);
      setShowBulkActionsMenu(false);
    }
  };

  if (selectedCrew.length === 0) return null;

  return (
    <div className="relative inline-block text-left" ref={bulkActionsRef}>
      {/* FT-16: min-h-[44px] ensures 44px+ touch targets on mobile */}
      <button
        onClick={() => setShowBulkActionsMenu(!showBulkActionsMenu)}
        className="inline-flex items-center gap-2 px-4 py-3 md:py-1.5 text-base md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px]"
      >
        <UserCheck className="h-5 w-5 md:h-4 md:w-4" />
        Bulk Actions ({selectedCrew.length})
        <ChevronDown className="h-5 w-5 md:h-4 md:w-4" />
      </button>
      
      {/* Bulk Actions Dropdown Menu */}
      {showBulkActionsMenu && (
        <div className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            <button
              onClick={handleBulkSignOn}
              className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <CheckCircle className="mr-3 h-4 w-4 text-green-600" />
              Sign On Selected
            </button>
            <button
              onClick={handleBulkSignOff}
              className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <XCircle className="mr-3 h-4 w-4 text-gray-600" />
              Sign Off Selected
            </button>
            <button
              onClick={handleBulkRecordHOR}
              className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Clock className="mr-3 h-4 w-4 text-blue-600" />
              Record Hours of Rest
            </button>
            <div className="border-t border-gray-100"></div>
            <button
              onClick={handleBulkExport}
              className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Download className="mr-3 h-4 w-4 text-gray-600" />
              Export Selected
            </button>
            <div className="border-t border-gray-100"></div>
            <button
              onClick={handleBulkDelete}
              className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="mr-3 h-4 w-4" />
              Delete Selected
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrewBulkActions;